// ============================================================================
// adminKozlemenyView.js — "Közlemények" az admin panelben (2026-10-25): új közlemény küldése (mindenkinek / kijelölt tagoknak /
// egy szoba tagjainak), és az elküldöttek listája azzal, hogy ki vette tudomásul és ki nem. CSAK admin (a szerver is csak adminnak engedi).
// A műveletek hívásai a main.js-ben vannak; ez a nézet csak rajzol és a kezelőket hívja. A szöveg textContent-tel kerül ki.
// ============================================================================

import { t, localeFor } from "./i18n.js";
import { el } from "./dom.js";
import { getRoomDisplayName } from "./roomName.js";
import { szobaFaSorrend } from "./roomTree.js";

export const KOZLEMENY_MAX_KARAKTER = 600;
export const KOZLEMENY_NAPOK = [1, 3, 7, 14, 30, 60, 90];

function datum(ms, uiLang) {
  if (!ms) return "";
  try {
    return new Date(ms).toLocaleString(localeFor(uiLang), { dateStyle: "short", timeStyle: "short" });
  } catch (e) {
    return "";
  }
}

/**
 * @param {object} model { uiLang, contentLang, rooms, users, myUid, busy, kozlemeny: {text, audience, uids[], roomId, days, valasz, hiba} | null,
 *   kozlemenyek: null | Array }
 * @param {object} handlers onAnnChange(mezo, ertek), onAnnToggleUser(uid, bool), onAnnSend, onAnnRefresh, onAnnWithdraw(annId)
 */
export function renderKozlemenyek(model, handlers) {
  const { uiLang, contentLang, rooms, users, myUid, busy } = model;
  const f = model.kozlemeny || { text: "", audience: "all", uids: [], roomId: "", days: 30 };
  const szekcio = el("section", { class: "panel-section admin-announcements" });
  szekcio.appendChild(el("h3", { text: t(uiLang, "admin.announcements") }));
  szekcio.appendChild(el("p", { class: "hint", text: t(uiLang, "admin.annHint") }));

  // --- Új közlemény
  szekcio.appendChild(el("label", { class: "panel-label", text: t(uiLang, "admin.annText") }));
  const mezo = el("textarea", { class: "panel-input ann-text", rows: "4", maxlength: String(KOZLEMENY_MAX_KARAKTER), "aria-label": t(uiLang, "admin.annText") });
  mezo.value = f.text || "";
  const szamlalo = el("div", { class: "hint ann-counter", text: `${[...mezo.value].length} / ${KOZLEMENY_MAX_KARAKTER}` });
  mezo.addEventListener("input", () => {
    szamlalo.textContent = `${[...mezo.value].length} / ${KOZLEMENY_MAX_KARAKTER}`;
    handlers.onAnnChange("text", mezo.value);
  });
  szekcio.appendChild(mezo);
  szekcio.appendChild(szamlalo);

  const kor = el("div", { class: "ann-audience" });
  for (const [kulcs, felirat] of [["all", "admin.annAll"], ["users", "admin.annUsers"], ["room", "admin.annRoom"]]) {
    const cimke = el("label", { class: "panel-check ann-audience-opt" });
    const r = el("input", { type: "radio", name: "ann-audience", value: kulcs });
    r.checked = f.audience === kulcs;
    r.addEventListener("change", () => handlers.onAnnChange("audience", kulcs));
    cimke.appendChild(r);
    cimke.appendChild(el("span", { text: " " + t(uiLang, felirat) }));
    kor.appendChild(cimke);
  }
  szekcio.appendChild(kor);

  if (f.audience === "users") {
    const lista = el("div", { class: "panel-list ann-users" });
    const aktiv = (users || []).filter((u) => u.active && u.uid !== myUid);
    const kijelolt = new Set(f.uids || []);
    for (const u of aktiv) {
      const sor = el("label", { class: "panel-check" });
      const cb = el("input", { type: "checkbox" });
      cb.checked = kijelolt.has(u.uid);
      cb.addEventListener("change", () => handlers.onAnnToggleUser(u.uid, cb.checked));
      sor.appendChild(cb);
      sor.appendChild(el("span", { text: " " + (u.name || u.displayName || u.email || u.uid) }));
      lista.appendChild(sor);
    }
    szekcio.appendChild(lista);
  } else if (f.audience === "room") {
    const valaszto = el("select", { class: "panel-input", "aria-label": t(uiLang, "admin.annRoom") });
    valaszto.appendChild(el("option", { value: "", text: t(uiLang, "admin.topicsPickRoom") }));
    // Minden élő szoba (a fórum-módú és a hagyományos is): a közlemény a szoba TAGJAINAK megy.
    for (const { room, melyseg } of szobaFaSorrend((rooms || []).filter((r) => r && !r.deleting && !r.archived))) {
      const o = el("option", { value: room.roomId, text: "  ".repeat(melyseg) + (melyseg > 0 ? "↳ " : "") + getRoomDisplayName(room, contentLang, uiLang) });
      if (room.roomId === f.roomId) o.selected = true;
      valaszto.appendChild(o);
    }
    valaszto.addEventListener("change", () => handlers.onAnnChange("roomId", valaszto.value));
    szekcio.appendChild(valaszto);
  }

  szekcio.appendChild(el("label", { class: "panel-label small", text: t(uiLang, "admin.annDays") }));
  const napok = el("select", { class: "panel-input ann-days", "aria-label": t(uiLang, "admin.annDays") });
  for (const n of KOZLEMENY_NAPOK) {
    const o = el("option", { value: String(n), text: String(n) });
    if (n === Number(f.days)) o.selected = true;
    napok.appendChild(o);
  }
  napok.addEventListener("change", () => handlers.onAnnChange("days", Number(napok.value)));
  szekcio.appendChild(napok);

  if (f.hiba) szekcio.appendChild(el("div", { class: "panel-status err", text: f.hiba }));
  if (f.valasz) szekcio.appendChild(el("div", { class: "panel-status ok", text: f.valasz }));
  szekcio.appendChild(el("div", { class: "panel-row" }, [
    el("button", { type: "button", class: "primary-btn compact ann-send", text: t(uiLang, "admin.annSend"), disabled: busy, onclick: handlers.onAnnSend }),
  ]));

  // --- Az elküldöttek: ki vette tudomásul
  const fej = el("div", { class: "panel-row ann-list-head" });
  fej.appendChild(el("h3", { text: t(uiLang, "admin.annListTitle") }));
  fej.appendChild(el("button", { type: "button", class: "text-btn", text: t(uiLang, "search.refresh"), disabled: busy, onclick: handlers.onAnnRefresh }));
  szekcio.appendChild(fej);
  if (!Array.isArray(model.kozlemenyek)) {
    szekcio.appendChild(el("p", { class: "hint", text: t(uiLang, "common.loading") }));
    return szekcio;
  }
  if (model.kozlemenyek.length === 0) {
    szekcio.appendChild(el("p", { class: "hint", text: t(uiLang, "admin.annNone") }));
    return szekcio;
  }
  for (const k of model.kozlemenyek) {
    const sor = el("div", { class: "admin-ann-row" + (k.withdrawn ? " withdrawn" : "") });
    const cim = el("div", { class: "admin-ann-head" });
    cim.appendChild(el("span", { class: "admin-ann-date", text: datum(k.createdAt, uiLang) }));
    cim.appendChild(el("span", { class: "admin-topic-badge", text: t(uiLang, "admin.annAcked", { a: k.ackCount, n: k.recipientCount }) }));
    if (k.withdrawn) cim.appendChild(el("span", { class: "admin-topic-badge", text: t(uiLang, "admin.annWithdrawn") }));
    sor.appendChild(cim);
    sor.appendChild(el("div", { class: "admin-ann-text", text: k.text }));
    const reszlet = el("details", { class: "admin-ann-detail" });
    reszlet.appendChild(el("summary", { text: "👥" }));
    const nevek = el("ul", { class: "admin-ann-names" });
    for (const c of k.recipients || []) {
      nevek.appendChild(el("li", { class: c.ackedAt ? "ack" : "pending", text: (c.ackedAt ? "✔ " : "⏳ ") + (c.name || c.uid) + (c.ackedAt ? " · " + datum(c.ackedAt, uiLang) : "") }));
    }
    reszlet.appendChild(nevek);
    sor.appendChild(reszlet);
    if (!k.withdrawn) {
      sor.appendChild(el("button", { type: "button", class: "danger-btn compact", text: t(uiLang, "admin.annWithdraw"), disabled: busy, onclick: () => handlers.onAnnWithdraw(k.annId) }));
    }
    szekcio.appendChild(sor);
  }
  return szekcio;
}
